import { useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";

import { FormField } from "components/form/FormField/FormField";
import { Notice, NoticeText } from "components/ui/Notice";
import { getServerErrorMessage } from "utils/showMutationError";

import { SelectGroup } from "../../../SelectGroup/SelectGroup";
import { SelectHospital } from "../../../SelectHospital/SelectHospital";
import { scrollToFirstError } from "../../utils/scrollToFirstError";
import { ItemFormWrapper } from "../ItemFormWrapper/ItemFormWrapper";
import style from "./GeneralInformationForm.module.css";

const radioSx = {
  "&.Mui-checked": {
    color: "var(--green-900)",
  },
};

export const GeneralInformationForm = ({
  onBack,
  onContinue,
  form,
  hospitals,
  availableReferralTypes,
  isLoadingHospitals = false,
  hospitalsError = null,
}) => {
  const handleFormSubmit = (values) => {
    onContinue(values);
  };
  const [isPublic, setIsPublic] = useState(true);
  const [group, setGroup] = useState("");

  const filteredHospitals = useMemo(
    () =>
      hospitals.filter((hospital) => {
        if (isPublic) {
          return (
            hospital.hospitalTypeName === "Public" ||
            hospital.hospitalTypeName === "Community Service"
          );
        }

        return hospital.hospitalTypeName === "Private";
      }),
    [hospitals, isPublic]
  );

  const hospitalGroups = useMemo(() => {
    return filteredHospitals.reduce(
      (acc, hospital) => {
        const groupName = hospital.hospitalGroupName;

        if (groupName && groupName.trim() !== "") {
          if (!acc.grouped[groupName]) {
            acc.grouped[groupName] = [];
          }
          acc.grouped[groupName].push(hospital);
        } else {
          acc.ungrouped.push(hospital);
        }

        return acc;
      },
      { grouped: {}, ungrouped: [] }
    );
  }, [filteredHospitals]);

  const hospitalSelectOptions = useMemo(() => {
    const groupOptions = Object.entries(hospitalGroups.grouped).map(
      ([groupName]) => ({
        label: groupName,
        value: `G-${groupName}`,
      })
    );

    const ungroupedOptions = hospitalGroups.ungrouped.map((hospital) => ({
      label: hospital.hospitalName,
      value: `H-${hospital.hospitalId?.toString()}`,
    }));

    return [...groupOptions, ...ungroupedOptions].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
  }, [hospitalGroups]);

  return (
    <>
      <ItemFormWrapper
        onBack={onBack}
        onContinue={form.handleSubmit(handleFormSubmit, scrollToFirstError)}
      >
        <div
          className={style.itemWrapper}
          style={{
            marginBottom: "0px",
            paddingBottom: "0px",
            borderBottom: "none",
          }}
        >
          {!!hospitalsError && (
            <div className={style.servicesError}>
              <Notice testId="healthlink-services-error">
                <NoticeText isLead>
                  The list of hospitals couldn&apos;t be loaded, so there is
                  nothing to choose from.
                </NoticeText>
                <NoticeText>
                  {getServerErrorMessage(
                    hospitalsError,
                    "Please try again, and contact Kinesin support if it keeps failing."
                  )}
                </NoticeText>
              </Notice>
            </div>
          )}
          <div>
            <label className={style.addReferralFormLabel}>
              Choose hospital type
            </label>
            <RadioGroup
              options={[
                { label: "Public", value: "public" },
                { label: "Private", value: "private" },
              ]}
              defaultValue="public"
              onChange={(e) => {
                setIsPublic(e.target.value === "public");
                setGroup("");
                form.setValue("hospital", "");
                form.clearErrors("hospital");
                form.setValue("referralType", "");
                form.clearErrors("referralType");
              }}
              value={isPublic ? "public" : "private"}
            >
              <div className={style.radioGroup} style={{ marginTop: "-8px" }}>
                <FormControlLabel
                  value="public"
                  control={<Radio size="small" sx={radioSx} />}
                  label="Public"
                />
                <FormControlLabel
                  value="private"
                  control={<Radio size="small" sx={radioSx} />}
                  label="Private"
                />
              </div>
            </RadioGroup>
          </div>
          <FormProvider {...form}>
            <form
              className={style.addReferralForm}
              onSubmit={form.handleSubmit(handleFormSubmit, scrollToFirstError)}
              style={{ marginTop: "0px" }}
            >
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginBottom: "12px", marginTop: "8px" }}
              >
                <FormField name="hospital">
                  <label className={style.addReferralFormLabel}>Hospital</label>
                  <SelectGroup
                    options={hospitalSelectOptions}
                    value={group}
                    showX={group || form.watch("hospital")}
                    onChange={(v) => {
                      if (!v) {
                        setGroup("");
                        form.setValue("hospital", "");
                        form.clearErrors("hospital");
                        form.setValue("referralType", "");
                        form.clearErrors("referralType");

                        return;
                      }

                      const [type, value] = v.split("-");

                      if (type === "G") {
                        setGroup(value);
                        form.setValue("hospital", "");
                        form.clearErrors("hospital");
                        form.setValue("referralType", "");
                        form.clearErrors("referralType");
                      } else {
                        setGroup("");
                        form.setValue("hospital", value?.toString() ?? "");
                        form.clearErrors("hospital");
                        form.setValue("referralType", "");
                        form.clearErrors("referralType");
                      }
                    }}
                    required
                    isLoading={isLoadingHospitals}
                  />
                </FormField>
                {!!group && (
                  <FormField name="hospital">
                    <label className={style.addReferralFormLabel}>Clinic</label>
                    <SelectHospital
                      options={filteredHospitals
                        .filter((hospital) => {
                          if (group)
                            return hospital.hospitalGroupName === group;

                          return true;
                        })
                        .map((hospital) => ({
                          label: hospital.hospitalName.toString(),
                          value: hospital.hospitalId.toString(),
                        }))}
                      value={form.watch("hospital")}
                      onChange={(value) => {
                        form.setValue("hospital", value.toString());
                        form.clearErrors("hospital");
                        form.setValue("referralType", "");
                        form.clearErrors("referralType");
                      }}
                      required
                      isLoading={isLoadingHospitals}
                    />
                  </FormField>
                )}
                <FormField name="referralType">
                  <label className={style.addReferralFormLabel}>
                    Referral Type
                  </label>
                  <SelectHospital
                    options={availableReferralTypes.map((referralType) => ({
                      label: referralType.toString(),
                      value: referralType.toString(),
                    }))}
                    value={form.watch("referralType")}
                    onChange={(value) => {
                      form.setValue("referralType", value.toString());
                      form.clearErrors("referralType");
                    }}
                    required
                    isReferral
                  />
                </FormField>
              </div>
            </form>
          </FormProvider>
        </div>
      </ItemFormWrapper>
    </>
  );
};
